import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TrashIcon, WarningIcon } from "@phosphor-icons/react";
import { api, apiError, clearAuthTokens } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function DangerZone() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirmDelete = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.delete("/me/account");
      clearAuthTokens();
      setUser(null);
      setOpen(false);
      toast.success("Your account has been deleted. Sorry to see you go.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  return (
    <section className="mt-10 border border-red-200 rounded-2xl p-5 bg-red-50/40" data-testid="danger-zone">
      <div className="flex items-center gap-2 mb-2">
        <WarningIcon size={18} weight="fill" className="text-red-600" />
        <h2 className="font-display font-bold text-lg text-red-700">Danger zone</h2>
      </div>
      <p className="text-sm text-zinc-600 mb-4">
        Permanently delete your Bidorah account and personal data. This cannot be undone.
      </p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-red-300 bg-white text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 text-sm font-semibold transition-colors"
        data-testid="delete-account-btn"
      >
        <TrashIcon size={16} weight="bold" />
        Delete my account
      </button>

      <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove your profile, sign you out everywhere, and end any
              live streams. Order and payment records are kept only as required by law. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="delete-account-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-account-confirm-btn"
            >
              {busy ? "Deleting…" : "OK, delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
